import { inject, Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { EMPTY, finalize, map } from "rxjs";
import { catchError } from "rxjs/operators";
import { MapSearchError, MapSearchErrorTypes, MapSearchResult } from "../models/map-search.models";
import { MapSearchApiService } from "../services/map-search-api.service";
import { MapSearchActions } from "./map-search.actions";

export interface MapSearchStateModel {
    searchQuery: string | undefined;
    searchResults: MapSearchResult[] | undefined;
    isSearchProcessing: boolean;
    selectedSearchResult: MapSearchResult | undefined;
    error: MapSearchError<MapSearchErrorTypes> | undefined;
}

const defaultState: MapSearchStateModel = {
    searchQuery: undefined,
    searchResults: undefined,
    isSearchProcessing: false,
    selectedSearchResult: undefined,
    error: undefined,
};

@State<MapSearchStateModel>({
    name: "mapSearch",
    defaults: defaultState,
})
@Injectable()
export class MapSearchState {
    private readonly mapApiService = inject(MapSearchApiService);

    @Selector()
    public static searchQuery(state: MapSearchStateModel) {
        return state.searchQuery;
    }

    @Selector()
    public static searchResults(state: MapSearchStateModel) {
        return state.searchResults;
    }

    @Selector()
    public static isSearchProcessing(state: MapSearchStateModel) {
        return state.isSearchProcessing;
    }

    @Selector()
    public static selectedSearchResult(state: MapSearchStateModel) {
        return state.selectedSearchResult;
    }

    @Selector()
    public static error(state: MapSearchStateModel) {
        return state.error;
    }

    @Action(MapSearchActions.SetSearchQuery, { cancelUncompleted: true })
    public setSearchQuery(context: StateContext<MapSearchStateModel>, action: MapSearchActions.SetSearchQuery) {
        context.patchState({
            searchQuery: action.searchQuery,
            searchResults: undefined,
            selectedSearchResult: undefined,
            isSearchProcessing: false,
        });

        if (!action.searchQuery || action.searchQuery.length === 0) {
            return;
        }

        context.patchState({ isSearchProcessing: true });

        return this.mapApiService.getSearchResults(action.searchQuery).pipe(
            map((results) => context.patchState({ searchResults: results })),
            catchError((error) => {
                context.patchState({ error });

                return EMPTY;
            }),
            finalize(() => context.patchState({ isSearchProcessing: false }))
        );
    }

    @Action(MapSearchActions.SelectSearchResult)
    public selectSearchResult(context: StateContext<MapSearchStateModel>, action: MapSearchActions.SelectSearchResult) {
        context.patchState({ selectedSearchResult: action.searchResult });
    }
}
